/**
 * SalesDashboardChart Component - Phase 5 Implementation
 * Line chart for revenue trends in sales dashboard
 * Shows revenue over time with trend indicators and growth metrics
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../../ui/chart';
import { Skeleton } from '../../ui/skeleton';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface SalesDashboardChartProps {
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const chartConfig = {
  revenue: {
    label: "Revenue",
    color: "hsl(var(--chart-1))",
  },
  orders: {
    label: "Orders",
    color: "hsl(var(--chart-2))",
  },
  customers: {
    label: "New Customers", 
    color: "hsl(var(--chart-3))",
  },
} satisfies ChartConfig;

// Mock data for demonstration
const generateMockData = () => [
  { date: '2024-01-01', revenue: 45000, orders: 120, customers: 25, growth: 5.2 },
  { date: '2024-01-02', revenue: 52000, orders: 135, customers: 32, growth: 15.6 },
  { date: '2024-01-03', revenue: 48000, orders: 128, customers: 28, growth: 6.7 },
  { date: '2024-01-04', revenue: 61000, orders: 145, customers: 35, growth: 27.1 },
  { date: '2024-01-05', revenue: 55000, orders: 140, customers: 30, growth: 22.2 },
  { date: '2024-01-06', revenue: 68000, orders: 158, customers: 42, growth: 51.1 },
  { date: '2024-01-07', revenue: 59000, orders: 142, customers: 33, growth: 31.1 },
  { date: '2024-01-08', revenue: 72000, orders: 165, customers: 45, growth: 60.0 },
  { date: '2024-01-09', revenue: 64000, orders: 150, customers: 38, growth: 42.2 },
  { date: '2024-01-10', revenue: 75000, orders: 172, customers: 48, growth: 66.7 },
  { date: '2024-01-11', revenue: 69000, orders: 155, customers: 40, growth: 53.3 },
  { date: '2024-01-12', revenue: 78000, orders: 180, customers: 52, growth: 73.3 },
  { date: '2024-01-13', revenue: 71000, orders: 160, customers: 44, growth: 57.8 },
  { date: '2024-01-14', revenue: 82000, orders: 188, customers: 55, growth: 82.2 },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[200px]">
        <p className="font-medium mb-2">{formatDate(label)}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between text-sm mb-1">
            <div className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
            </div>
            <span className="font-medium">
              {entry.dataKey === 'revenue' 
                ? formatCurrency(entry.value)
                : entry.value.toLocaleString()
              }
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const SalesDashboardChart: React.FC<SalesDashboardChartProps> = ({
  data,
  isLoading = false,
  error,
  className
}) => {
  // Use mock data if no data provided or in loading state
  const chartData = data.length > 0 ? data : generateMockData();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load chart</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <div className="flex space-x-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="flex space-x-6 justify-center">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const totalRevenue = chartData.reduce((sum, item) => sum + item.revenue, 0);
  const totalOrders = chartData.reduce((sum, item) => sum + item.orders, 0);
  const avgGrowth = chartData.reduce((sum, item) => sum + item.growth, 0) / chartData.length;
  const isPositiveGrowth = avgGrowth > 0;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold text-chart-1">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
            <div className="text-chart-1">
              <TrendingUp className="h-8 w-8" />
            </div>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Orders</p>
              <p className="text-2xl font-bold text-chart-2">
                {totalOrders.toLocaleString()}
              </p>
            </div>
            <div className="text-chart-2">
              <TrendingUp className="h-8 w-8" />
            </div>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Growth</p>
              <p className={cn(
                "text-2xl font-bold",
                isPositiveGrowth ? "text-green-600" : "text-red-600"
              )}>
                {avgGrowth.toFixed(1)}%
              </p>
            </div>
            <div className={cn(
              isPositiveGrowth ? "text-green-600" : "text-red-600"
            )}>
              {isPositiveGrowth ? (
                <TrendingUp className="h-8 w-8" />
              ) : (
                <TrendingDown className="h-8 w-8" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <ChartContainer config={chartConfig} className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              yAxisId="revenue"
              orientation="left"
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              yAxisId="orders"
              orientation="right"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<ChartLegendContent />} />
            
            <Line
              yAxisId="revenue"
              type="monotone"
              dataKey="revenue"
              stroke="var(--color-revenue)"
              strokeWidth={3}
              dot={{ fill: "var(--color-revenue)", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 2 }}
              name="Revenue"
            />
            <Line
              yAxisId="orders"
              type="monotone"
              dataKey="orders"
              stroke="var(--color-orders)"
              strokeWidth={2}
              dot={{ fill: "var(--color-orders)", strokeWidth: 2, r: 3 }}
              strokeDasharray="5 5"
              name="Orders"
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};